import React, { useContext, useEffect, useState, useRef } from 'react';
import {
	Box,
	CircularProgress,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Button,
	FormControl,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import { getStageStatistics } from '../../api/content-api';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import OlympicsContext from '../../store/olympics-context';
import { DEFAULT_API_ERROR } from '../../utils/auth-tools';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';

// Function to create 4-letter abbreviation from district name (without Polish characters)
const getDistrictAbbreviation = (name: string): string => {
	// Remove Polish characters
	const polishMap: { [key: string]: string } = {
		ą: 'a',
		ć: 'c',
		ę: 'e',
		ł: 'l',
		ń: 'n',
		ó: 'o',
		ś: 's',
		ź: 'z',
		ż: 'z',
		Ą: 'A',
		Ć: 'C',
		Ę: 'E',
		Ł: 'L',
		Ń: 'N',
		Ó: 'O',
		Ś: 'S',
		Ź: 'Z',
		Ż: 'Z',
	};

	let normalized = name;
	for (const [polish, latin] of Object.entries(polishMap)) {
		normalized = normalized.replace(new RegExp(polish, 'g'), latin);
	}

	// Remove spaces and special characters, take first 4 letters
	const cleaned = normalized.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
	return cleaned.substring(0, 4).padEnd(4, ' ');
};

interface DistrictStats {
	district_id: number;
	district_name: string;
	total_students: number;
	male_count: number;
	female_count: number;
	// Etap I
	approved_count?: number;
	not_entered_count?: number;
	unapproved_with_result_count?: number;
	approved_no_result_count?: number;
	delivered_count?: number;
	not_delivered_count?: number;
	graded_count?: number;
	qualified_count?: number;
	// Etap II
	entered_2a_count?: number;
	not_entered_2a_count?: number;
	graded_2a_count?: number;
	qualified_2a_count?: number;
	entered_2b_count?: number;
	not_entered_2b_count?: number;
	graded_2b_count?: number;
	qualified_2b_count?: number;
	// Etap III
	entered_3a_count?: number;
	not_entered_3a_count?: number;
	graded_3a_count?: number;
	qualified_3a_count?: number;
	finalists_3a_count?: number;
	entered_3b_count?: number;
	not_entered_3b_count?: number;
	graded_3b_count?: number;
	finalists_count?: number;
	laureates_3_count?: number;
	laureates_2_count?: number;
	laureates_1_count?: number;
}

interface PointsDistribution {
	points: number;
	type?: string; // '2a', '2b', 'total', '3a', '3b' for stage 2 and 3
	[key: string]: number | string | undefined; // district_X and total
}

interface DistrictInfo {
	id: number;
	name: string;
}

interface TopicAnalysis {
	topic: string;
	count?: number;
	total_count?: number;
	average_points: number;
	percentile_25: number | null;
	percentile_50: number | null;
	percentile_75: number | null;
	percentile_95: number | null;
	// dodatkowe statystyki dla części pisemnych i ustnych
	written1_min?: number | null;
	written1_avg?: number | null;
	written1_max?: number | null;
	written2_min?: number | null;
	written2_avg?: number | null;
	written2_max?: number | null;
	oral_min?: number | null;
	oral_avg?: number | null;
	oral_max?: number | null;
}

interface TopicsAnalysisData {
	by_district: Array<{
		district_id: number;
		district_name: string;
		topics: TopicAnalysis[];
	}>;
	overall: TopicAnalysis[];
}

interface StageStatisticsData {
	stage: string;
	timestamp: string;
	by_district: DistrictStats[];
	points_distribution: PointsDistribution[] | null;
	points_distribution_districts?: DistrictInfo[];
	topics_analysis?: TopicsAnalysisData | null;
}

const StageStatistics = () => {
	const { token, isAdmin } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const { refreshTablesTrigger, setIsRefreshing } = useContext(OlympicsContext);
	const [loading, setLoading] = useState(true);
	const [statistics, setStatistics] = useState<StageStatisticsData | null>(null);
	const [selectedStage, setSelectedStage] = useState('first');
	const [showPointsDistribution, setShowPointsDistribution] = useState(false);
	const [showTopicsAnalysis, setShowTopicsAnalysis] = useState(false);
	const pointsDistributionRef = useRef<HTMLDivElement>(null);
	const topicsAnalysisRef = useRef<HTMLDivElement>(null);
	const statsMountRef = useRef(true);

	const loadStatistics = async (isFromPanelHeaderRefresh = false) => {
		if (!token || !isAdmin) return;

		if (isFromPanelHeaderRefresh) {
			setIsRefreshing(true);
		}
		setLoading(true);
		try {
			const response = await getStageStatistics(token, selectedStage, showPointsDistribution, showTopicsAnalysis);
			setStatistics(response.data);
		} catch (error: any) {
			console.error('Error loading stage statistics:', error);
			setMsg({ msg: DEFAULT_API_ERROR, severity: 'error' });
		} finally {
			setLoading(false);
			if (isFromPanelHeaderRefresh) {
				setIsRefreshing(false);
			}
		}
	};

	useEffect(() => {
		if (isAdmin) {
			loadStatistics(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStage, showPointsDistribution, showTopicsAnalysis, isAdmin, token]);

	useEffect(() => {
		if (!isAdmin) {
			return;
		}
		if (statsMountRef.current) {
			statsMountRef.current = false;
			return;
		}
		if (refreshTablesTrigger > 0) {
			loadStatistics(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger]);

	const handleStageChange = (event: SelectChangeEvent) => {
		setSelectedStage(event.target.value);
	};

	const handleRefresh = () => {
		loadStatistics(false);
	};

	const renderTableHeaders = () => {
		if (selectedStage === 'first') {
			return (
				<>
					<TableCell>
						<strong>Okręg</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Łącznie</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pan</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pani</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Potwierdzeni</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie wprowadzeni</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie potwierdzeni</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Brak wyniku</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Dostarczone</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Niedostarczone</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Ocenione</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Zakwalifikowani</strong>
					</TableCell>
				</>
			);
		} else if (selectedStage === 'second') {
			return (
				<>
					<TableCell>
						<strong>Okręg</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Łącznie</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pan</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pani</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Wprowadzeni do 2A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie wprowadzeni do 2A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Ocenieni w 2A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Zakwalifikowani z 2A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Wprowadzeni do 2B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie wprowadzeni do 2B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Ocenieni w 2B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Zakwalifikowani z 2B</strong>
					</TableCell>
				</>
			);
		} else if (selectedStage === 'third') {
			return (
				<>
					<TableCell>
						<strong>Okręg</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Łącznie</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pan</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Pani</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Wprowadzeni do 3A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie wprowadzeni do 3A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Ocenieni w 3A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Zakwalifikowani z 3A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Finaliści z 3A</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Wprowadzeni do 3B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Nie wprowadzeni do 3B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Ocenieni w 3B</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Finaliści</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Laureaci III</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Laureaci II</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>Laureaci I</strong>
					</TableCell>
				</>
			);
		}
		return null;
	};

	const renderTableRow = (district: DistrictStats, index: number, isTotal: boolean = false) => {
		if (selectedStage === 'first') {
			return (
				<TableRow
					key={isTotal ? 'total' : district.district_id}
					sx={{
						backgroundColor: isTotal ? 'action.hover' : index % 2 === 0 ? 'transparent' : 'action.hover',
					}}
				>
					<TableCell>
						<strong>{isTotal ? 'RAZEM' : district.district_name}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.total_students}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.male_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.female_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.approved_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_entered_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: (district.unapproved_with_result_count || 0) > 0 ? '#d32f2f' : 'inherit' }}>
							{district.unapproved_with_result_count || 0}
						</strong>
					</TableCell>
					<TableCell align='right'>
						<strong
							style={{
								color: (district.approved_no_result_count || 0) > 0 ? '#1976d2' : 'inherit',
								fontWeight: (district.approved_no_result_count || 0) > 0 ? 'bold' : 'normal',
							}}
						>
							{district.approved_no_result_count || 0}
						</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.delivered_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_delivered_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.graded_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: '#1976d2' }}>{district.qualified_count || 0}</strong>
					</TableCell>
				</TableRow>
			);
		} else if (selectedStage === 'second') {
			return (
				<TableRow
					key={isTotal ? 'total' : district.district_id}
					sx={{
						backgroundColor: isTotal ? 'action.hover' : index % 2 === 0 ? 'transparent' : 'action.hover',
					}}
				>
					<TableCell>
						<strong>{isTotal ? 'RAZEM' : district.district_name}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.total_students}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.male_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.female_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.entered_2a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_entered_2a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.graded_2a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: '#1976d2' }}>{district.qualified_2a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.entered_2b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_entered_2b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.graded_2b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: '#1976d2' }}>{district.qualified_2b_count || 0}</strong>
					</TableCell>
				</TableRow>
			);
		} else if (selectedStage === 'third') {
			return (
				<TableRow
					key={isTotal ? 'total' : district.district_id}
					sx={{
						backgroundColor: isTotal ? 'action.hover' : index % 2 === 0 ? 'transparent' : 'action.hover',
					}}
				>
					<TableCell>
						<strong>{isTotal ? 'RAZEM' : district.district_name}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.total_students}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.male_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.female_count}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.entered_3a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_entered_3a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.graded_3a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: '#1976d2' }}>{district.qualified_3a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.finalists_3a_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.entered_3b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.not_entered_3b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.graded_3b_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong style={{ color: '#1976d2' }}>{district.finalists_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.laureates_3_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.laureates_2_count || 0}</strong>
					</TableCell>
					<TableCell align='right'>
						<strong>{district.laureates_1_count || 0}</strong>
					</TableCell>
				</TableRow>
			);
		}
		return null;
	};

	const calculateTotals = () => {
		if (!statistics) {
			return {
				total_students: 0,
				male_count: 0,
				female_count: 0,
			} as any;
		}
		return statistics.by_district.reduce(
			(acc, district) => {
				const totals: any = {
					total_students: acc.total_students + district.total_students,
					male_count: acc.male_count + district.male_count,
					female_count: acc.female_count + district.female_count,
				};

				if (selectedStage === 'first') {
					totals.approved_count = (acc.approved_count || 0) + (district.approved_count || 0);
					totals.not_entered_count = (acc.not_entered_count || 0) + (district.not_entered_count || 0);
					totals.unapproved_with_result_count =
						(acc.unapproved_with_result_count || 0) + (district.unapproved_with_result_count || 0);
					totals.approved_no_result_count =
						(acc.approved_no_result_count || 0) + (district.approved_no_result_count || 0);
					totals.delivered_count = (acc.delivered_count || 0) + (district.delivered_count || 0);
					totals.not_delivered_count = (acc.not_delivered_count || 0) + (district.not_delivered_count || 0);
					totals.graded_count = (acc.graded_count || 0) + (district.graded_count || 0);
					totals.qualified_count = (acc.qualified_count || 0) + (district.qualified_count || 0);
				} else if (selectedStage === 'second') {
					totals.entered_2a_count = (acc.entered_2a_count || 0) + (district.entered_2a_count || 0);
					totals.not_entered_2a_count = (acc.not_entered_2a_count || 0) + (district.not_entered_2a_count || 0);
					totals.graded_2a_count = (acc.graded_2a_count || 0) + (district.graded_2a_count || 0);
					totals.qualified_2a_count = (acc.qualified_2a_count || 0) + (district.qualified_2a_count || 0);
					totals.entered_2b_count = (acc.entered_2b_count || 0) + (district.entered_2b_count || 0);
					totals.not_entered_2b_count = (acc.not_entered_2b_count || 0) + (district.not_entered_2b_count || 0);
					totals.graded_2b_count = (acc.graded_2b_count || 0) + (district.graded_2b_count || 0);
					totals.qualified_2b_count = (acc.qualified_2b_count || 0) + (district.qualified_2b_count || 0);
				} else if (selectedStage === 'third') {
					totals.entered_3a_count = (acc.entered_3a_count || 0) + (district.entered_3a_count || 0);
					totals.not_entered_3a_count = (acc.not_entered_3a_count || 0) + (district.not_entered_3a_count || 0);
					totals.graded_3a_count = (acc.graded_3a_count || 0) + (district.graded_3a_count || 0);
					totals.qualified_3a_count = (acc.qualified_3a_count || 0) + (district.qualified_3a_count || 0);
					totals.finalists_3a_count = (acc.finalists_3a_count || 0) + (district.finalists_3a_count || 0);
					totals.entered_3b_count = (acc.entered_3b_count || 0) + (district.entered_3b_count || 0);
					totals.not_entered_3b_count = (acc.not_entered_3b_count || 0) + (district.not_entered_3b_count || 0);
					totals.graded_3b_count = (acc.graded_3b_count || 0) + (district.graded_3b_count || 0);
					totals.finalists_count = (acc.finalists_count || 0) + (district.finalists_count || 0);
					totals.laureates_3_count = (acc.laureates_3_count || 0) + (district.laureates_3_count || 0);
					totals.laureates_2_count = (acc.laureates_2_count || 0) + (district.laureates_2_count || 0);
					totals.laureates_1_count = (acc.laureates_1_count || 0) + (district.laureates_1_count || 0);
				}

				return totals;
			},
			{
				total_students: 0,
				male_count: 0,
				female_count: 0,
			} as any,
		);
	};

	if (!isAdmin) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography
					variant='h6'
					color='error'
				>
					Dostęp tylko dla administratorów
				</Typography>
			</Box>
		);
	}

	if (loading && !statistics) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Grid
				container
				spacing={2}
				sx={{ mb: 3 }}
			>
				<Grid
					item
					xs={12}
					md={6}
				>
					<FormControl fullWidth>
						<Select
							value={selectedStage}
							onChange={handleStageChange}
							displayEmpty
						>
							<MenuItem value='first'>Etap I</MenuItem>
							<MenuItem value='second'>Etap II</MenuItem>
							<MenuItem value='third'>Etap III</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid
					item
					xs={12}
					md={6}
					sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
				>
					<Button
						variant='outlined'
						startIcon={<RefreshIcon />}
						onClick={handleRefresh}
						disabled={loading}
					>
						Odśwież
					</Button>
					<Button
						variant={showPointsDistribution ? 'contained' : 'outlined'}
						startIcon={<BarChartIcon />}
						onClick={() => {
							const newValue = !showPointsDistribution;
							setShowPointsDistribution(newValue);
							// Scroll to points distribution after state update
							if (newValue && pointsDistributionRef.current) {
								setTimeout(() => {
									pointsDistributionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
								}, 100);
							}
						}}
					>
						{showPointsDistribution ? 'Ukryj' : 'Pokaż'} rozkład punktów
					</Button>
					{(selectedStage === 'second' || selectedStage === 'third') && (
						<Button
							variant={showTopicsAnalysis ? 'contained' : 'outlined'}
							startIcon={<BarChartIcon />}
							onClick={() => {
								const newValue = !showTopicsAnalysis;
								setShowTopicsAnalysis(newValue);
								// Scroll to topics analysis after state update
								if (newValue && topicsAnalysisRef.current) {
									setTimeout(() => {
										topicsAnalysisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
									}, 100);
								}
							}}
						>
							{showTopicsAnalysis ? 'Ukryj' : 'Pokaż'} analizę tematów
						</Button>
					)}
				</Grid>
			</Grid>

			{statistics && (
				<>
					<Typography
						variant='h6'
						sx={{ mb: 2 }}
					>
						Statystyki - {selectedStage === 'first' ? 'Etap I' : selectedStage === 'second' ? 'Etap II' : 'Etap III'}
					</Typography>
					<Typography
						variant='caption'
						color='text.secondary'
						sx={{ mb: 2, display: 'block' }}
					>
						Ostatnia aktualizacja: {new Date(statistics.timestamp).toLocaleString('pl-PL')}
					</Typography>

					<TableContainer
						component={Paper}
						sx={{ mb: 3 }}
					>
						<Table
							size='small'
							sx={{
								'& .MuiTableCell-root': {
									padding: '8px 16px',
								},
							}}
						>
							<TableHead>
								<TableRow>{renderTableHeaders()}</TableRow>
							</TableHead>
							<TableBody>
								{(() => {
									const totals = calculateTotals();
									const colSpan = selectedStage === 'first' ? 12 : selectedStage === 'second' ? 12 : 16;

									return (
										<>
											{/* Summary row */}
											{renderTableRow(totals as DistrictStats, -1, true)}
											{/* District rows */}
											{statistics.by_district.map((district, index) => renderTableRow(district, index, false))}
											{statistics.by_district.length === 0 && (
												<TableRow>
													<TableCell
														colSpan={colSpan}
														align='center'
													>
														<Typography color='text.secondary'>Brak danych</Typography>
													</TableCell>
												</TableRow>
											)}
											{/* Duplicate header at the end */}
											{statistics.by_district.length > 0 && (
												<TableRow
													sx={{ backgroundColor: 'action.hover', borderTop: '2px solid', borderColor: 'divider' }}
												>
													{renderTableHeaders()}
												</TableRow>
											)}
										</>
									);
								})()}
							</TableBody>
						</Table>
					</TableContainer>

					{showPointsDistribution && statistics.points_distribution && statistics.points_distribution.length > 0 && (
						<Box
							ref={pointsDistributionRef}
							sx={{ mt: 3 }}
						>
							<Typography
								variant='h6'
								sx={{ mb: 2 }}
							>
								Rozkład punktów
							</Typography>
							<TableContainer
								component={Paper}
								sx={{
									maxWidth: '100%',
									overflowX: 'auto',
								}}
							>
								<Table
									size='small'
									stickyHeader
									sx={{
										'& .MuiTableCell-root': {
											padding: '8px 16px',
										},
									}}
								>
									<TableHead>
										<TableRow>
											<TableCell>
												<strong>Punkty</strong>
											</TableCell>
											{(selectedStage === 'second' || selectedStage === 'third') && (
												<TableCell>
													<strong>Typ</strong>
												</TableCell>
											)}
											{statistics.points_distribution_districts?.map((district) => {
												const abbreviation = getDistrictAbbreviation(district.name);
												return (
													<TableCell
														key={district.id}
														align='right'
														title={district.name}
													>
														<strong>{abbreviation}</strong>
													</TableCell>
												);
											})}
											<TableCell align='right'>
												<strong>Razem</strong>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{statistics.points_distribution.map((item, index) => (
											<TableRow
												key={index}
												sx={{
													backgroundColor: index % 2 === 0 ? 'transparent' : 'action.hover',
												}}
											>
												<TableCell>
													<strong>{item.points}</strong>
												</TableCell>
												{(selectedStage === 'second' || selectedStage === 'third') && (
													<TableCell>
														{item.type === '2a'
															? '2A (pisemny)'
															: item.type === '2b'
																? '2B (ustny)'
																: item.type === 'total'
																	? 'Suma łączna'
																	: item.type === '3a'
																		? '3A (pisemny)'
																		: item.type === '3b'
																			? '3B (ustny)'
																			: item.type === '3full'
																				? 'Etap III (suma łączna)'
																				: '-'}
													</TableCell>
												)}
												{statistics.points_distribution_districts?.map((district) => {
													const raw = item[`district_${district.id}`];
													const count = typeof raw === 'number' ? raw : 0;
													return (
														<TableCell
															key={district.id}
															align='right'
														>
															{count > 0 ? count : '-'}
														</TableCell>
													);
												})}
												<TableCell align='right'>
													<strong>{item.total || 0}</strong>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}

					{showTopicsAnalysis && statistics.topics_analysis && (
						<Box
							ref={topicsAnalysisRef}
							sx={{ mt: 3 }}
						>
							<Typography
								variant='h6'
								sx={{ mb: 2 }}
							>
								Analiza tematów
							</Typography>

							{/* Overall topics statistics */}
							{statistics.topics_analysis.overall && statistics.topics_analysis.overall.length > 0 && (
								<Box sx={{ mb: 3 }}>
									<Typography
										variant='subtitle1'
										sx={{ mb: 1 }}
									>
										Ogólne statystyki tematów
									</Typography>
									<TableContainer
										component={Paper}
										sx={{
											maxWidth: '100%',
											overflowX: 'auto',
										}}
									>
										<Table
											size='small'
											sx={{
												'& .MuiTableCell-root': {
													padding: '8px 16px',
												},
											}}
										>
											<TableHead>
												<TableRow>
													<TableCell>
														<strong>Temat</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Liczba wyborów</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Średnia (suma pisemna)</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Min pisemna I</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Śr. pisemna I</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Max pisemna I</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Min pisemna II</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Śr. pisemna II</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Max pisemna II</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Min ustna</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Śr. ustna</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Max ustna</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Percentyl 25%</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Mediana (50%)</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Percentyl 75%</strong>
													</TableCell>
													<TableCell align='right'>
														<strong>Percentyl 95%</strong>
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{statistics.topics_analysis.overall.map((topic, index) => (
													<TableRow
														key={topic.topic}
														sx={{
															backgroundColor: index % 2 === 0 ? 'transparent' : 'action.hover',
														}}
													>
														<TableCell>
															<strong>{topic.topic}</strong>
														</TableCell>
														<TableCell align='right'>{topic.total_count || topic.count || 0}</TableCell>
														<TableCell align='right'>{topic.average_points.toFixed(2)}</TableCell>
														<TableCell align='right'>
															{topic.written1_min != null ? topic.written1_min.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.written1_avg != null ? topic.written1_avg.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.written1_max != null ? topic.written1_max.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.written2_min != null ? topic.written2_min.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.written2_avg != null ? topic.written2_avg.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.written2_max != null ? topic.written2_max.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.oral_min != null ? topic.oral_min.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.oral_avg != null ? topic.oral_avg.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.oral_max != null ? topic.oral_max.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.percentile_25 !== null ? topic.percentile_25.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.percentile_50 !== null ? topic.percentile_50.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.percentile_75 !== null ? topic.percentile_75.toFixed(2) : '-'}
														</TableCell>
														<TableCell align='right'>
															{topic.percentile_95 !== null ? topic.percentile_95.toFixed(2) : '-'}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}

							{/* Topics by district */}
							{statistics.topics_analysis.by_district && statistics.topics_analysis.by_district.length > 0 && (
								<Box>
									<Typography
										variant='subtitle1'
										sx={{ mb: 1 }}
									>
										Statystyki tematów w okręgach
									</Typography>
									{statistics.topics_analysis.by_district.map((districtData) => (
										<Box
											key={districtData.district_id}
											sx={{ mb: 3 }}
										>
											<Typography
												variant='subtitle2'
												sx={{ mb: 1 }}
											>
												{districtData.district_name}
											</Typography>
											<TableContainer
												component={Paper}
												sx={{
													maxWidth: '100%',
													overflowX: 'auto',
												}}
											>
												<Table
													size='small'
													sx={{
														'& .MuiTableCell-root': {
															padding: '8px 16px',
														},
													}}
												>
													<TableHead>
														<TableRow>
															<TableCell>
																<strong>Temat</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Liczba wyborów</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Średnia (suma pisemna)</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Min pisemna I</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Śr. pisemna I</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Max pisemna I</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Min pisemna II</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Śr. pisemna II</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Max pisemna II</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Min ustna</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Śr. ustna</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Max ustna</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Percentyl 25%</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Mediana (50%)</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Percentyl 75%</strong>
															</TableCell>
															<TableCell align='right'>
																<strong>Percentyl 95%</strong>
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{districtData.topics.map((topic, index) => (
															<TableRow
																key={topic.topic}
																sx={{
																	backgroundColor: index % 2 === 0 ? 'transparent' : 'action.hover',
																}}
															>
																<TableCell>
																	<strong>{topic.topic}</strong>
																</TableCell>
																<TableCell align='right'>{topic.count || topic.total_count || 0}</TableCell>
																<TableCell align='right'>{topic.average_points.toFixed(2)}</TableCell>
																<TableCell align='right'>
																	{topic.written1_min != null ? topic.written1_min.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.written1_avg != null ? topic.written1_avg.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.written1_max != null ? topic.written1_max.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.written2_min != null ? topic.written2_min.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.written2_avg != null ? topic.written2_avg.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.written2_max != null ? topic.written2_max.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.oral_min != null ? topic.oral_min.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.oral_avg != null ? topic.oral_avg.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.oral_max != null ? topic.oral_max.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.percentile_25 !== null ? topic.percentile_25.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.percentile_50 !== null ? topic.percentile_50.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.percentile_75 !== null ? topic.percentile_75.toFixed(2) : '-'}
																</TableCell>
																<TableCell align='right'>
																	{topic.percentile_95 !== null ? topic.percentile_95.toFixed(2) : '-'}
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</Box>
									))}
								</Box>
							)}
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

export default StageStatistics;
